import { Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import { CONTACT } from "@/constants/testIds";
import { telHref, waHref, mailHref, PHONE, EMAIL } from "@/constants/site";
import Reveal from "@/components/Reveal";
import { useT } from "@/i18n/LanguageContext";
import useCallTracking from "@/hooks/useCallTracking";

export default function Contact() {
  const { t } = useT();
  const trackCall = useCallTracking();

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative bg-[#F5F5F7] py-16 sm:py-24 lg:py-32 border-t border-black/5"
    >
      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
        <Reveal>
          <div className="text-center">
            <div className="overline text-[#1E63FF]">{t.contact.overline}</div>
            <h2 className="font-display font-semibold text-3xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A0A0A] mt-4 leading-[1.02]">
              {t.contact.heading1}<br />
              <span className="text-[#1E63FF]">{t.contact.heading2}</span>
            </h2>
            <p className="mt-5 sm:mt-6 text-neutral-600 text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
              {t.contact.intro}
            </p>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <div className="mt-10 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <ContactRow
              testid={CONTACT.phone}
              icon={Phone}
              label={t.contact.phoneLabel}
              value={PHONE}
              href={telHref}
              onClick={trackCall("contact-row")}
              accent
            />
            <ContactRow
              testid={CONTACT.whatsapp}
              icon={MessageCircle}
              label={t.contact.whatsappLabel}
              value={t.contact.whatsappValue}
              href={waHref()}
              external
            />
            <ContactRow
              testid={CONTACT.email}
              icon={Mail}
              label={t.contact.emailLabel}
              value={EMAIL}
              href={mailHref}
            />
            <ContactRow
              icon={MapPin}
              label={t.contact.serviceAreaLabel}
              value={t.contact.serviceAreaValue}
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ContactRow({ testid, icon: Icon, label, value, href, accent, external, onClick }) {
  const inner = (
    <div className={`group flex items-center gap-4 p-4 rounded-xl border border-black/10 bg-white transition-all ${href ? "hover:border-[#0A0A0A] hover:-translate-y-0.5" : ""}`}>
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${accent ? "bg-[#1E63FF]" : "bg-[#0A0A0A]"} text-white`}>
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <div className="min-w-0">
        <div className="overline text-neutral-500">{label}</div>
        <div className="text-[15px] font-semibold text-[#0A0A0A] truncate">{value}</div>
      </div>
    </div>
  );

  if (!href) return <div data-testid={testid}>{inner}</div>;
  return (
    <a
      data-testid={testid}
      href={href}
      onClick={onClick}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className="block"
    >
      {inner}
    </a>
  );
}
